import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Upload, X, LogOut, PackageOpen, FileUp, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AdminStats } from "../components/AdminStats";
import { AdminOrders } from "../components/AdminOrders";
import { AdminCustomers } from "../components/AdminCustomers";
import { AdminRevenue } from "../components/AdminRevenue";
import api from "../lib/api";
import { PushToggle } from "../components/PushToggle";
import { useStoreSettings } from "../context/StoreSettingsContext";

/* Interruttore modalità vetrina: acquisti ON/OFF per tutto il negozio */
const ShopModeRow = () => {
  const { shopEnabled, refresh } = useStoreSettings();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { shop_enabled: !shopEnabled });
      await refresh();
      toast.success(shopEnabled ? "Modalità vetrina attiva: acquisti disattivati" : "Negozio aperto: acquisti attivi");
    } catch {
      toast.error("Errore nel cambio modalità");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-4 ${shopEnabled ? "border-ink/10 bg-panel" : "border-amber/40 bg-amber/10"}`} data-testid="shop-mode-row">
      <div>
        <p className="text-sm font-semibold text-ink">{shopEnabled ? "Negozio aperto agli acquisti" : "Modalità vetrina attiva"}</p>
        <p className="text-xs text-ink-soft">
          {shopEnabled ? "I clienti possono ordinare e pagare." : "I clienti vedono il catalogo ma non possono acquistare."}
        </p>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="shop-mode-toggle"
        className={`relative h-8 w-14 rounded-full transition-colors ${shopEnabled ? "bg-sage" : "bg-ink/20"} disabled:opacity-50`}
        aria-label="Attiva o disattiva gli acquisti"
      >
        <span className={`absolute top-1 h-6 w-6 rounded-full bg-cream shadow transition-all ${shopEnabled ? "left-7" : "left-1"}`} />
      </button>
    </div>
  );
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const euro = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;
const resolveImg = (src) => (src?.startsWith("http") ? src : `${BACKEND}${src}`);

const empty = { name: "", category: "casa", price: "", unit: "pz", description: "", image: "", images: [], tags: [], recurring: false, available: true, stock: "", flash_sale: false, flash_end: "", flash_discount: "", is_new: false, produttore: "", show_produttore: false };
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, ...}
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("prodotti");
  const [focusOrder, setFocusOrder] = useState(null);

  const openOrder = (id) => {
    setFocusOrder(id);
    setTab("ordini");
  };
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === null) navigate("/login");
    else if (user && user.role !== "admin") { toast.error("Area riservata"); navigate("/"); }
  }, [user, navigate]);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
    setProducts(p.data);
    setCategories(c.data);
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        urls.push(data.url);
      }
      setEditing((prev) => {
        const images = [...(prev.images || []), ...urls];
        return { ...prev, images, image: prev.image || urls[0] };
      });
      toast.success(urls.length > 1 ? `${urls.length} foto caricate` : "Foto caricata");
    } catch { toast.error("Upload non riuscito"); } finally { setUploading(false); }
  };

  const removeImage = (url) => {
    setEditing((prev) => {
      const images = (prev.images || []).filter((u) => u !== url);
      const image = prev.image === url ? (images[0] || "") : prev.image;
      return { ...prev, images, image };
    });
  };

  const setMainImage = (url) => setEditing((prev) => ({ ...prev, image: url }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing, price: parseFloat(editing.price) };
    payload.stock = (editing.stock === "" || editing.stock === null || editing.stock === undefined) ? null : parseInt(editing.stock, 10);
    payload.flash_sale = !!editing.flash_sale;
    payload.flash_discount = (editing.flash_discount === "" || editing.flash_discount == null) ? null : parseInt(editing.flash_discount, 10);
    payload.flash_end = editing.flash_end ? new Date(editing.flash_end).toISOString() : null;
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Prodotto salvato");
      setEditing(null);
      load();
    } catch { toast.error("Salvataggio non riuscito"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/admin/box-reminders");
      toast.success(`Promemoria inviati: ${data.sent}`);
    } catch { toast.error("Invio promemoria non riuscito"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/import", fd);
      if (data.errors?.length) {
        toast.warning(`Creati ${data.created}, aggiornati ${data.updated}. Errori: ${data.errors.slice(0, 3).join(" · ")}`);
      } else {
        toast.success(`Importazione completata: ${data.created} creati, ${data.updated} aggiornati`);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Importazione non riuscita"); }
  };

  if (user === undefined || (user && user.role !== "admin")) {
    return <div className="grid min-h-screen place-items-center bg-cream"><p className="font-serif text-2xl text-ink">Caricamento…</p></div>;
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream">
      <div className="safe-pt sticky top-0 z-40 border-b border-ink/10 bg-cream/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} data-testid="admin-back-button" aria-label="Torna indietro" className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 text-ink hover:bg-ink hover:text-cream">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
            </button>
            <Link to="/" className="notranslate font-serif text-3xl text-ink" translate="no">NEED</Link>
            <span className="rounded-full bg-ink px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-cream">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/account" className="text-sm text-ink hover:text-sage">Account</Link>
            <PushToggle label="Notifiche ordini" />
            <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-sm text-ink hover:bg-ink hover:text-cream" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <ShopModeRow />
        <AdminStats onCardClick={(t) => setTab(t)} />

        {/* Schede sezioni */}
        <div className="mb-8 flex gap-2 overflow-x-auto rounded-full border border-ink/10 bg-panel p-1.5" data-testid="admin-tabs">
          {[["prodotti", "Prodotti"], ["ordini", "Ordini"], ["clienti", "Clienti"], ["incassi", "Incassi"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`flex-1 whitespace-nowrap rounded-full px-3 py-2.5 text-xs font-semibold transition-colors sm:px-6 sm:text-sm ${tab === key ? "bg-ink text-cream" : "text-ink-soft hover:text-ink"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "prodotti" && (
        <>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-4xl font-light text-ink">Prodotti</h1>
            <p className="mt-1 text-sm text-ink-soft" data-testid="admin-product-count">{products.length} prodotti a catalogo</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={sendReminders} data-testid="box-reminders-button" className="rounded-full border border-ink/15 px-5 py-3 text-sm font-medium text-ink hover:bg-ink hover:text-cream">
              Promemoria box
            </button>
            <label title="Colonne CSV: name, price, category, unit, description, stock, recurring, is_new, available, image (opzionale)" className="flex cursor-pointer items-center gap-2 rounded-full border border-ink/15 px-5 py-3 text-sm font-medium text-ink hover:bg-ink hover:text-cream" data-testid="import-csv-button">
              <FileUp className="h-4 w-4" /> Importa CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="import-csv-input" />
            </label>
            <button onClick={() => setEditing({ ...empty })} data-testid="new-product-button" className="flex items-center gap-2 rounded-full bg-sage px-6 py-3 text-sm font-semibold text-cream hover:bg-sage-deep">
              <Plus className="h-4 w-4" /> Nuovo prodotto
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink/10 bg-panel">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-4 border-b border-ink/5 px-4 py-3 last:border-0" data-testid={`admin-row-${p.id}`}>
              <img src={resolveImg(p.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{p.name}</p>
                <p className="text-sm text-ink-soft">{p.category} · {euro(p.price)} · Pezzi disponibili: {p.stock != null ? p.stock : "illimitati"} {p.recurring && "· mensile"} {!p.available && "· ESAURITO"}</p>
              </div>
              <button onClick={() => setEditing({ ...p })} data-testid={`edit-${p.id}`} className="grid h-9 w-9 place-items-center rounded-full text-ink hover:bg-ink/5"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="grid h-9 w-9 place-items-center rounded-full text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {products.length === 0 && <p className="p-8 text-center text-ink-soft">Nessun prodotto.</p>}
        </div>
        </>
        )}

        {tab === "ordini" && <AdminOrders focusOrder={focusOrder} onFocusConsumed={() => setFocusOrder(null)} />}
        {tab === "clienti" && <AdminCustomers onOpenOrder={openOrder} />}
        {tab === "incassi" && <AdminRevenue onOpenOrder={openOrder} />}
      </div>

      {/* Edit/Create drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/40 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <motion.form
            initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="safe-pt safe-pb flex h-full w-full max-w-md flex-col overflow-y-auto bg-cream p-6 sm:max-w-2xl md:p-8"
            data-testid="product-form"
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Modifica prodotto" : "Nuovo prodotto"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-ink/10"><X className="h-5 w-5" /></button>
            </div>

            <label className="mb-4 block">
              <span className="text-xs uppercase tracking-overline text-ink-soft">Foto prodotto (puoi caricarne più di una)</span>
              <div className="mt-2 flex flex-wrap items-start gap-3">
                {(editing.images || []).length === 0 && !editing.image && (
                  <div className="grid h-24 w-24 place-items-center rounded-xl border border-ink/15 bg-panel">
                    <PackageOpen className="h-8 w-8 text-ink-soft" />
                  </div>
                )}
                {[...new Set([editing.image, ...(editing.images || [])].filter(Boolean))].map((url) => (
                  <div key={url} className={`relative overflow-hidden rounded-xl border-2 ${url === editing.image ? "border-sage" : "border-ink/15"}`} data-testid={`thumb-${url.split("/").pop()}`}>
                    <img src={resolveImg(url)} alt="" onClick={() => setMainImage(url)} className="h-24 w-24 cursor-pointer object-contain" title="Clicca per renderla la foto principale" />
                    {url === editing.image && <span className="absolute bottom-0 left-0 right-0 bg-sage/90 py-0.5 text-center text-[9px] font-bold uppercase text-cream">Principale</span>}
                    <button type="button" onClick={() => removeImage(url)} data-testid={`remove-img-${url.split("/").pop()}`} className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-ink/80 text-cream hover:bg-terra">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-ink/25 text-ink-soft transition-colors hover:border-sage hover:text-sage">
                  <Upload className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{uploading ? "Carico…" : "Aggiungi"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="image-upload-input" />
                </label>
              </div>
              <span className="mt-1.5 block text-[11px] text-ink-soft">Clicca una miniatura per renderla la foto principale. La X la elimina.</span>
            </label>

            <Field label="Nome"><input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="form-name" className="input" /></Field>
            <Field label="Categoria">
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="form-category" className="input">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prezzo (€)"><input required type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="form-price" className="input" /></Field>
              <Field label="Unità"><input required value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} data-testid="form-unit" className="input" /></Field>
            </div>
            <Field label="Descrizione"><textarea required rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="form-description" className="input resize-none" /></Field>
            <Field label="Produttore / Marchio (opzionale)"><input value={editing.produttore ?? ""} onChange={(e) => setEditing({ ...editing, produttore: e.target.value })} data-testid="form-produttore" placeholder="es. Fabuloso, Scottex…" className="input" /></Field>
            <label className="mb-4 -mt-2 flex items-center gap-3 text-sm text-ink">
              <input type="checkbox" checked={editing.show_produttore ?? false} onChange={(e) => setEditing({ ...editing, show_produttore: e.target.checked })} data-testid="form-show-produttore" className="h-4 w-4 accent-sage" />
              Mostra il produttore sul sito
            </label>
            <Field label="Scorta disponibile (vuoto = illimitata)"><input type="number" min="0" value={editing.stock ?? ""} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="form-stock" placeholder="es. 10" className="input" /></Field>

            <div className="mb-6 mt-2 flex flex-col gap-3">
              <label className="flex items-center gap-3 text-sm text-ink">
                <input type="checkbox" checked={editing.recurring} onChange={(e) => setEditing({ ...editing, recurring: e.target.checked })} data-testid="form-recurring" className="h-4 w-4 accent-sage" />
                Essenziale mensile (ricorrente)
              </label>
              <label className="flex items-center gap-3 text-sm text-ink">
                <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} data-testid="form-available" className="h-4 w-4 accent-sage" />
                Disponibile
              </label>
              <label className="flex items-center gap-3 text-sm text-ink">
                <input type="checkbox" checked={editing.is_new} onChange={(e) => setEditing({ ...editing, is_new: e.target.checked })} data-testid="form-isnew" className="h-4 w-4 accent-lime" />
                Novità (badge)
              </label>
            </div>

            <div className="mb-6 rounded-xl border border-ink/10 bg-panel2 p-4">
              <label className="flex items-center gap-3 text-sm text-ink">
                <input type="checkbox" checked={editing.flash_sale} onChange={(e) => setEditing({ ...editing, flash_sale: e.target.checked })} data-testid="form-flash" className="h-4 w-4 accent-terra" />
                Offerta lampo (badge rosso + countdown)
              </label>
              {editing.flash_sale && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Sconto %"><input type="number" min="1" max="90" value={editing.flash_discount ?? ""} onChange={(e) => setEditing({ ...editing, flash_discount: e.target.value })} data-testid="form-flash-discount" className="input" /></Field>
                  <Field label="Fine offerta"><input type="datetime-local" value={toLocalInput(editing.flash_end)} onChange={(e) => setEditing({ ...editing, flash_end: e.target.value })} data-testid="form-flash-end" className="input" /></Field>
                </div>
              )}
            </div>

            <button type="submit" disabled={saving} data-testid="save-product-button" className="mt-auto w-full rounded-full bg-ink py-3.5 text-sm font-semibold text-cream hover:bg-sage disabled:opacity-60">
              {saving ? "Salvataggio…" : "Salva prodotto"}
            </button>
          </motion.form>
        </div>
      )}

      <style>{`.input{margin-top:6px;width:100%;border-radius:12px;border:1px solid rgba(244,242,236,.15);background:#161520;padding:10px 14px;font-size:14px;outline:none}.input:focus{border-color:#FF6A2C}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="mb-4 block">
    <span className="text-xs uppercase tracking-overline text-ink-soft">{label}</span>
    {children}
  </label>
);
